import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageTitle, Stat } from "@/components/app/ui";
import { CheckoutBox } from "@/pages/site/Checkout";
import { useCurrency } from "@/context/CurrencyContext";
import { api, fmtMoney } from "@/lib/api";

export default function Credits() {
  const { currency, config } = useCurrency();
  const [d, setD] = useState(null);
  const [buy, setBuy] = useState(null);
  const load = () => api.get("/app/credits").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="h-64 rounded-2xl bg-muted animate-pulse" />;
  return (
    <div data-testid="credits-page">
      <PageTitle title="Credits Wallet" copy="AI usage is metered per task. Costs are shown before each task, and failed tasks are never charged." />
      {d.low && <div className="mb-6 flex items-center gap-3 rounded-2xl border border-[#f59e0b]/40 bg-[#f59e0b]/10 p-4 text-sm" data-testid="credits-low-alert"><AlertTriangle className="h-5 w-5 text-[#f59e0b]" /> Your balance is low. Top up to keep generating content.</div>}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Balance" value={d.balance.toLocaleString()} color="#7c3aed" testId="credits-balance" />
        <Stat label="Monthly allowance" value={d.monthly_allowance.toLocaleString()} color="#06b6d4" />
        <Stat label="Credits saved by reuse" value={(d.saved || 0).toLocaleString()} color="#f59e0b" testId="credits-saved" hint="shared intelligence, repurposing & Gemini routing" />
        <Stat label="Billing source" value="Sliance credits" color="#10b981" hint="Bring-your-own AI key: Coming Soon" />
      </div>
      <h3 className="mt-10 mb-4 text-xl font-semibold">Top up</h3>
      <div className="grid sm:grid-cols-3 gap-4">
        {config && Object.entries(config.packs).map(([id, p]) => (
          <button key={id} onClick={() => setBuy(id)} className="card-box p-6 text-left lift" data-testid={`topup-${id}`}>
            <div className="font-semibold">{p.name}</div>
            <div className="mt-1 text-2xl font-extrabold text-gradient">{fmtMoney(p.prices[currency], currency, config.symbols)}</div>
          </button>
        ))}
      </div>
      <h3 className="mt-10 mb-4 text-xl font-semibold">Usage ledger</h3>
      <div className="card-box overflow-x-auto">
        <Table data-testid="credits-ledger">
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Task</TableHead><TableHead>Model</TableHead><TableHead>Source</TableHead><TableHead className="text-right">Credits</TableHead></TableRow></TableHeader>
          <TableBody>{d.ledger.map((l) => (
            <TableRow key={l.id}><TableCell className="text-xs">{new Date(l.created_at).toLocaleString()}</TableCell><TableCell>{l.task}</TableCell><TableCell className="text-xs">{l.model}</TableCell><TableCell className="text-xs">{l.billing}</TableCell>
              <TableCell className={`text-right font-semibold ${l.credits < 0 ? "text-red-500" : "text-[#10b981]"}`}>{l.credits > 0 ? "+" : ""}{l.credits}</TableCell></TableRow>
          ))}</TableBody>
        </Table>
      </div>
      <Dialog open={!!buy} onOpenChange={(o) => !o && setBuy(null)}>
        <DialogContent className="max-w-lg p-0 border-0 bg-transparent"><DialogHeader className="sr-only"><DialogTitle>Buy credits</DialogTitle></DialogHeader>
          {buy && <CheckoutBox type="credits" item={buy} onDone={() => { setBuy(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
